// header.tsx - Optimized Header with Auth Integration
import React, { useState } from 'react';
import { useAuth } from '@client/contexts/auth-context';
import { Button } from "@client/components/ui/button";
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@client/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@client/components/ui/avatar";
import { LogOut, User, Settings, ChevronDown, Loader2 } from "lucide-react";

interface HeaderProps {
  title: string;
  description?: string;
}

// Alternative Simple Header (if you prefer the original style)
export function Header({ title, description }: HeaderProps) {
  const { user, logout } = useAuth();
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const displayName = user?.displayName || user?.username || 'Benutzer';

  const handleLogout = async () => {
    setIsLoggingOut(true);
    try {
      await logout();
    } finally {
      setIsLoggingOut(false);
    }
  };

  if (!user) return null;

  return (
    <header className="bg-white dark:bg-gray-900 shadow-sm border-b border-slate-200 dark:border-gray-700 px-6 py-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 dark:text-white">{title}</h1>
          {description && (
            <p className="text-sm text-slate-500 dark:text-gray-400">{description}</p>
          )}
        </div>
        
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2 text-sm text-slate-600">
            <User className="h-4 w-4" />
            <span>{displayName}</span>
          </div>
          
          <Button 
            onClick={handleLogout}
            disabled={isLoggingOut}
          >
            {isLoggingOut ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Abmelden...
              </>
            ) : (
              <>
                <LogOut className="h-4 w-4 mr-2" />
                Abmelden
              </>
            )}
          </Button>
        </div>
      </div>
    </header>
  );
}